import React from 'react';
import { connect } from 'react-redux';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import { suppressedShortfallListFetch, suppressedShortfallListSetPage, suppressedShortfallListUnload } from '../../lib/redux/actions/suppressedShortfalls';
import MASpinner from '../../components/common/MASpinner';
import Paginator from '../../components/common/Paginator';
import * as ROUTES from '../../lib/routing/frontend';
import SuppressedShortfallListTable from './SuppressedShortfallListTable';

const mapStateToProps = (state) => ({
  ...state.suppressedShortfalls,
});

const mapDispatchToProps = {
  suppressedShortfallListFetch,
  suppressedShortfallListSetPage,
  suppressedShortfallListUnload,
};

/**
 * MED-1002 - the Suppressed Shortfalls screen.
 *
 * Without it, `noShortfall` is a silent hole: an invoice never chased, a patient never told they
 * owe money, and nobody aware it happened. This is the screen that makes going quiet on somebody
 * a visible act rather than a configuration detail.
 *
 * ## No filters, deliberately
 *
 * The patient index needs a search box because it holds 57,624 people. This list holds one row
 * per withheld shortfall, suppression is meant to be the rare exception, and the useful question
 * is "what has gone quiet lately" - which is what newest-first answers. Filters can be added when
 * the list is long enough to need them, and not before.
 *
 * ## An empty list is the good outcome
 *
 * So it says so, rather than showing the "nothing found" wording every other index uses. And it
 * is only shown once a fetch has actually completed - `hasLoaded` rather than `!isFetching` -
 * because reassuring somebody that nothing was suppressed before asking the API would be the one
 * wrong thing this screen could do.
 */
class SuppressedShortfallListContainer extends React.Component {
  componentDidMount() {
    this.props.suppressedShortfallListFetch(this.getQueryParamPage());
  }

  componentWillUnmount() {
    this.props.suppressedShortfallListUnload();
  }

  componentDidUpdate(prevProps) {
    const { currentPage, suppressedShortfallListFetch, suppressedShortfallListSetPage } = this.props;

    if (prevProps.match.params.page !== this.getQueryParamPage()) {
      suppressedShortfallListSetPage(this.getQueryParamPage());
    }
    if (prevProps.currentPage !== currentPage) {
      suppressedShortfallListFetch(currentPage);
    }
  }

  getQueryParamPage() {
    return Number(this.props.match.params.page) || 1;
  }

  changePage(page) {
    const { history, suppressedShortfallListSetPage } = this.props;
    suppressedShortfallListSetPage(page);
    history.push(ROUTES.SUPPRESSED_SHORTFALLS.ALL.replace(':page', page));
  }

  nextPage() {
    const { currentPage, pageCount } = this.props;
    this.changePage(Math.min(currentPage + 1, pageCount));
  }

  prevPage() {
    const { currentPage } = this.props;
    this.changePage(Math.max(currentPage - 1, 1));
  }

  render() {
    const { suppressedShortfalls, isFetchingSuppressedShortfalls, hasLoadedSuppressedShortfalls, error, pageTitle, pageCount, currentPage } = this.props;
    const hasSuppressedShortfalls = suppressedShortfalls && suppressedShortfalls.length > 0;

    return (
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between">
            {pageTitle}
            {!isFetchingSuppressedShortfalls && hasSuppressedShortfalls && <span className="tabular-data-result-count">{pageCount} pages</span>}
          </div>
        </Card.Header>

        <Card.Body>
          <p className="text-muted mb-0">
            Shortfall notifications that were <strong>not</strong> sent to the patient, because that patient is marked &ldquo;no shortfall&rdquo; for the
            consultant on the invoice. The admin address was told instead. Clearing the flag on the patient record stops future suppressions; it does not remove
            anything from this list.
          </p>
        </Card.Body>

        {isFetchingSuppressedShortfalls && (
          <Card.Body>
            <MASpinner />
          </Card.Body>
        )}

        {!isFetchingSuppressedShortfalls && error && (
          <Card.Body>
            <Alert variant={'danger'} className="mb-0">
              <i className="fas fa-exclamation-triangle" /> Suppressed shortfalls could not be loaded.
            </Alert>
          </Card.Body>
        )}

        {!isFetchingSuppressedShortfalls && !error && hasLoadedSuppressedShortfalls && !hasSuppressedShortfalls && (
          <Card.Body>
            <Alert variant={'success'} className="mb-0">
              <i className="fas fa-check-circle" /> No shortfall notifications have been suppressed.
            </Alert>
          </Card.Body>
        )}

        {!isFetchingSuppressedShortfalls && hasSuppressedShortfalls && <SuppressedShortfallListTable suppressedShortfalls={suppressedShortfalls} />}

        {!isFetchingSuppressedShortfalls && hasSuppressedShortfalls && (
          <Paginator
            currentPage={currentPage}
            pageCount={pageCount}
            setPage={this.changePage.bind(this)}
            nextPage={this.nextPage.bind(this)}
            prevPage={this.prevPage.bind(this)}
          />
        )}
      </Card>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SuppressedShortfallListContainer);
